"use client";

import { useState, useEffect, useCallback } from "react";
import { Link } from "@/i18n/navigation";
import { usePathname } from "@/i18n/navigation";

interface MenuGroup {
  label: string;
  items: { title: string; href: string }[];
}

interface MobileMenuProps {
  groups: MenuGroup[];
  ctaLabel?: string;
  ctaHref?: string;
}

export function MobileMenu({ groups, ctaLabel = "Portal", ctaHref = "/dashboard" }: MobileMenuProps) {
  const [open, setOpen] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const pathname = usePathname();

  // Close on route change
  useEffect(() => {
    setOpen(false);
    setExpandedGroup(null);
  }, [pathname]);

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const toggleGroup = useCallback((label: string) => {
    setExpandedGroup((prev) => (prev === label ? null : label));
  }, []);

  return (
    <div className="md:hidden">
      {/* Hamburger button */}
      <button
        type="button"
        className="relative w-10 h-10 flex items-center justify-center rounded-lg hover:bg-surface-2 transition-colors"
        onClick={() => setOpen((prev) => !prev)}
        aria-label={open ? "Close menu" : "Open menu"}
        aria-expanded={open}
      >
        <div className="w-5 h-4 flex flex-col justify-between">
          <span
            className={`block h-0.5 w-5 bg-txt-secondary rounded-full transition-all duration-300 origin-center ${
              open ? "translate-y-[7px] rotate-45" : ""
            }`}
          />
          <span
            className={`block h-0.5 w-5 bg-txt-secondary rounded-full transition-all duration-300 ${
              open ? "opacity-0 scale-x-0" : ""
            }`}
          />
          <span
            className={`block h-0.5 w-5 bg-txt-secondary rounded-full transition-all duration-300 origin-center ${
              open ? "-translate-y-[7px] -rotate-45" : ""
            }`}
          />
        </div>
      </button>

      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 bg-black/50 z-40 animate-fade-in"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Slide-out panel */}
      <div
        className={`fixed inset-y-0 right-0 w-80 max-w-[calc(100vw-3rem)] bg-surface-1 border-l border-edge shadow-2xl z-50 flex flex-col transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 h-16 border-b border-edge/50 shrink-0">
          <span className="text-sm font-bold font-display">
            <span className="text-txt-primary">ChainEvidence</span>
          </span>
          <button
            type="button"
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-2 transition-colors"
            onClick={() => setOpen(false)}
            aria-label="Close menu"
          >
            <svg className="w-4 h-4 text-txt-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Accordion groups */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {groups.map((group) => (
            <div key={group.label} className="mb-1">
              <button
                type="button"
                className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-semibold text-txt-primary font-display rounded-lg hover:bg-surface-2 transition-colors"
                onClick={() => toggleGroup(group.label)}
                aria-expanded={expandedGroup === group.label}
              >
                {group.label}
                <svg
                  className={`w-4 h-4 text-txt-muted transition-transform duration-200 ${
                    expandedGroup === group.label ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                </svg>
              </button>

              <div
                className={`overflow-hidden transition-all duration-200 ${
                  expandedGroup === group.label ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <div className="pl-3 py-1 space-y-0.5">
                  {group.items.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      className="block px-3 py-2 text-sm text-txt-secondary hover:text-txt-primary hover:bg-surface-2 rounded-lg transition-colors"
                    >
                      {item.title}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </nav>

        {/* CTA button at bottom */}
        <div className="shrink-0 p-4 border-t border-edge/50">
          <Link
            href={ctaHref}
            className="block w-full text-center px-4 py-2.5 text-sm font-medium text-surface-0 bg-accent-500 hover:bg-accent-400 rounded-lg transition-colors"
          >
            {ctaLabel}
          </Link>
        </div>
      </div>
    </div>
  );
}
